// The night watch's reads as hooks, polled at the camera pages' pace (60 s, paused while the tab is
// hidden: components/cameras/useCameraData usePolling). Shared by the desktop Cameras page and the
// phone Cameras screen. Each answer is securityFetch's {ok, status, body, message}; a failed read
// keeps the last good one and says why in `error`.
//
// A refused PIN (401 / 403) stops every night-watch read until the session's PIN changes: each refused
// read is a miss on jobq-api's per-IP limiter, and every device at the shop shares one IP, so a tab
// left polling with a stale PIN would lock every admin there out within five minutes.
import { useCallback, useEffect, useState } from 'react';
import { usePolling } from '../../components/cameras/useCameraData';
import { adminPinFromSession, needsAdminPin } from '../../utils/securityApi';
import { fetchNightWatchAlerts, fetchNightWatchStatus, fetchNightWatchTimeline } from './nightWatch';
import { STATUS_POLL_MS, statusPollMs } from './doors';

// The session PIN jobq-api last refused (shared by every read on the page), or undefined.
let refusedPin;

/** Tests only: forget the refused PIN. */
export function resetRefusedPin() {
  refusedPin = undefined;
}

export const REFUSED_MESSAGE = 'JobQ refused this tab\'s Admin PIN. Unlock again with your PIN to see the night watch.';

function useSecurityRead(load, active, ms = 60000) {
  const [state, setState] = useState({ data: null, error: '', status: null });
  const run = useCallback(async () => {
    const pin = adminPinFromSession();
    if (refusedPin !== undefined && pin === refusedPin) {
      setState((prev) => (prev.error === REFUSED_MESSAGE ? prev : { ...prev, error: REFUSED_MESSAGE, status: 401 }));
      return;
    }
    const r = await load();
    if (needsAdminPin(r)) refusedPin = pin;
    setState((prev) => (r.ok ? { data: r.body, error: '', status: r.status }
      : { data: prev.data, error: r.message || 'Could not reach JobQ.', status: r.status }));
  }, [load]);
  usePolling(run, ms, active);
  return { ...state, reload: run };
}

/** GET /security/status (the pill), every minute while `active`; every 10 s while a door holds an alert
 *  or the siren is sounding (doors.statusPollMs), back to every minute after. */
export function useNightWatchStatus(active = true) {
  const load = useCallback(() => fetchNightWatchStatus(), []);
  const [ms, setMs] = useState(STATUS_POLL_MS);
  const r = useSecurityRead(load, active, ms);
  const next = statusPollMs(r.data);
  useEffect(() => { setMs(next); }, [next]);
  return { status: r.data, error: r.error, httpStatus: r.status, reload: r.reload, pollMs: ms };
}

/** GET /security/alerts since `since` (ISO), every minute while `active` (or every `ms`: the phone's recent
 *  list follows the status cadence). `limit` = the API's (default 200). */
export function useNightWatchAlerts(active, since, { limit = 200, ms = STATUS_POLL_MS } = {}) {
  const load = useCallback(() => fetchNightWatchAlerts({ since, limit }), [since, limit]);
  const r = useSecurityRead(load, active && !!since, ms);
  return { alerts: r.data?.alerts || null, more: !!r.data?.more, error: r.error, reload: r.reload };
}

/** GET /security/timeline for [since, until), every minute while `active`. */
export function useNightWatchTimeline(active, since, until) {
  const load = useCallback(() => fetchNightWatchTimeline({ since, until }), [since, until]);
  const r = useSecurityRead(load, active && !!since && !!until);
  return { timeline: r.data, error: r.error, reload: r.reload };
}
